import React from 'react';
import { ScrollValueType, ScrollSaverContext, SetSavedScrollProps } from './ScrollSaverContext';
import { ScrollSaverMode } from '../ScrollSaver/types';

interface ScrollSaverContextProviderProps {
  activePanel: string;
  children?: React.ReactNode;
}

/**
 * Провайдер для работы с сохранёнными значениями скролла отдельных компонентов.
 * Работает в связке с {@link ScrollSaver}. Для корректной работы необходимо обернуть Root
 *
 * @param activePanel - id активной панели
 */

export class ScrollSaverProvider extends React.Component<ScrollSaverContextProviderProps, any> {
  private readonly savedScroll: {
    [panelId: string]:
      | { [scrollId: string]: { value: ScrollValueType | undefined; saveMode: ScrollSaverMode } }
      | undefined;
  };
  private readonly panelTree: { [panelId: string]: number };
  private forcedActivePanel: string | undefined;

  constructor(props: ScrollSaverContextProviderProps) {
    super(props);

    this.savedScroll = {};
    this.panelTree = this.parseChildrenTree();
  }

  componentDidUpdate(prevProps: Readonly<ScrollSaverContextProviderProps>) {
    if (prevProps.activePanel !== this.props.activePanel) {
      const to = this.props.activePanel;
      const from = prevProps.activePanel;

      const toIndex = this.panelTree[to];
      const fromIndex = this.panelTree[from];

      const isForwardToPanel = toIndex > fromIndex;

      this.clearPanelScrolls(to, isForwardToPanel);
    }
  }

  /* Парсинг иерархии панелей, не зависимо от View */

  parseChildrenTree = () => {
    const result: { [panelId: string]: number } = {};
    let index = 0;
    React.Children.forEach((this.props.children as any).props.children, (viewChild) => {
      React.Children.forEach(viewChild.props.children, (panelChild) => {
        result[panelChild.props.id] = index;
        index += 1;
      });
    });

    return result;
  };

  /**
   * @param id - id скролла
   * @param panelId - id панели, на которой расположен сохраняемый скролл
   */
  getSavedScroll = (id: string, panelId?: string): ScrollValueType | undefined => {
    const resultPanel = panelId || this.forcedActivePanel || this.props.activePanel;

    return this.savedScroll[resultPanel]?.[id]?.value;
  };

  setSavedScroll = (props: SetSavedScrollProps) => {
    const { id, panelId, value, saveMode } = props;

    const resultPanel = panelId || this.forcedActivePanel || this.props.activePanel;

    this.savedScroll[resultPanel] = {
      ...this.savedScroll[resultPanel],
      [id]: { value, saveMode },
    };
  };

  /* Очистка сохранённых значений */

  private readonly clearPanelScrolls = (panelId: string, isForwardToPanel: boolean) => {
    const panelScrolls = this.savedScroll[panelId];
    if (!panelScrolls) {
      return;
    }
    for (const [scrollId, { saveMode }] of Object.entries(panelScrolls)) {
      if (saveMode === 'forward' && isForwardToPanel) {
        delete panelScrolls[scrollId];
      }
    }
  };

  render() {
    const { children } = this.props;

    return (
      <ScrollSaverContext.Provider
        value={{
          getSavedScroll: this.getSavedScroll,
          setSavedScroll: this.setSavedScroll,
        }}
      >
        {React.cloneElement(children as any, {
          children: React.Children.map((children as any).props.children, (viewChild) => {
            return React.cloneElement(viewChild, {
              onSwipeBackStart: (activePanel: string) => {
                const history = viewChild.props.history;

                if (history && history.length > 1) {
                  this.forcedActivePanel = history[history.length - 2];
                }

                if (viewChild.props.onSwipeBackStart) {
                  return viewChild.props.onSwipeBackStart(activePanel);
                }
              },
              onSwipeBackCancel: () => {
                this.forcedActivePanel = undefined;
                viewChild.props.onSwipeBackCancel && viewChild.props.onSwipeBackCancel();
              },
              onSwipeBack: () => {
                this.forcedActivePanel = undefined;
                viewChild.props.onSwipeBack && viewChild.props.onSwipeBack();
              },
            });
          }),
        })}
      </ScrollSaverContext.Provider>
    );
  }
}
